package com.evoapps.evoshift.viewmodels

import androidx.compose.ui.text.intl.Locale
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.DayRange
import com.evoapps.evoshift.domain.model.PrintedRoster
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.calendar.PrintRosterUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.print_not_allowed
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/**
 * "Print roster" (SH-261): [printed] once the PDF is made; [openUrl] while it waits to be opened
 * (the screen opens it and calls [RosterPrintViewModel.urlOpened]).
 */
data class RosterPrintState(
    val busy: Boolean = false,
    val error: UiText? = null,
    val printed: PrintedRoster? = null,
    val openUrl: String? = null
)

/**
 * "Print roster" (SH-261, roster builder): the owner or a manager prints the whole team's month on
 * one page, or a week of it in larger print, to pin up at work. The server (printRoster) checks the
 * same permission and makes the PDF in the app's [language]; the app opens its link.
 */
class RosterPrintViewModel(
    private val printRoster: PrintRosterUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val language: () -> String = { Locale.current.language }
) : ViewModel() {
    private val _state = MutableStateFlow(RosterPrintState())
    val state = _state.asStateFlow()

    /** A fresh dialog: no earlier PDF or error. */
    fun reset() {
        if (_state.value.busy) return
        _state.value = RosterPrintState()
    }

    /** The whole month ([week] null) or that week of it. */
    fun print(rosterId: String, week: DayRange?) {
        if (_state.value.busy) return
        _state.update { it.copy(busy = true, error = null, printed = null, openUrl = null) }
        viewModelScope.launch {
            val workplaceId = RosterIds.workplaceIdOf(rosterId)
            val allowed = capabilitiesAt(getCurrentUser(), observePlanActive, workplaceId).first().shows(Gate.PRINT_ROSTER)
            if (!allowed) {
                _state.update { it.copy(busy = false, error = UiText.Resource(Res.string.print_not_allowed)) }
                return@launch
            }
            printRoster(rosterId, week, language()).fold(
                onSuccess = { printed -> _state.update { it.copy(busy = false, printed = printed, openUrl = printed.url) } },
                onFailure = { e -> _state.update { it.copy(busy = false, error = e.toUiText()) } }
            )
        }
    }

    /** "Open" again (the browser may have blocked the first one). */
    fun open() = _state.update { it.copy(openUrl = it.printed?.url) }

    fun urlOpened() = _state.update { it.copy(openUrl = null) }
}

/**
 * The weeks of [year]-[month] to print one of (SH-261), each from the workplace's
 * [firstDayOfWeek] (1 = Monday) to the day before the next, cut to the month: the first and the
 * last may be shorter.
 */
fun printWeeks(year: Int, month: Int, firstDayOfWeek: Int): List<DayRange> =
    weeksOf(year, month, firstDayOfWeek).map { start ->
        val days = (0..6).map { start.plus(DatePeriod(days = it)) }.filter { it.year == year && it.month.ordinal + 1 == month }
        DayRange(days.first().day, days.last().day)
    }

/** The week [today] falls in when it is in [weeks]' month, else the first whole week (else the first). */
fun defaultPrintWeek(weeks: List<DayRange>, year: Int, month: Int, today: LocalDate): DayRange? =
    weeks.firstOrNull { today.year == year && today.month.ordinal + 1 == month && today.day in it.fromDay..it.toDay }
        ?: weeks.firstOrNull { it.toDay - it.fromDay == 6 } ?: weeks.firstOrNull()
